// Liturgical season + color resolution, computed locally with romcal.
//
// Previously this parsed a USCCB day-title string scraped by a Netlify
// function. USCCB now sits behind a Fastly bot challenge that 403s datacenter
// IPs, so in production the title never arrived and the theme sat on its indigo
// fallback. romcal does the Computus math itself — no network, nothing to
// block, works offline — and gives season and color as real fields instead of
// substring guesses.
//
// Season enum (unchanged — src/index.css [data-season] blocks target these):
//   'ordinary' | 'advent-lent' | 'red' | 'rose' | 'feast' | null
//
// Chrome comes from the SEASON, accent from the day's COLOR. That keeps the
// rule that seasons drive the app background while single-day observances only
// move the accent. null is reserved for "resolution failed", which omits
// data-season entirely and holds :root's indigo.

import Romcal from 'romcal';

export const DEFAULT_LITURGICAL_COLOR = '#B8860B'; // gold — used only when resolution fails

const NO_SEASON = { season: null, color: DEFAULT_LITURGICAL_COLOR, accent: null };

export const SEASON_COLORS = {
  ordinary: '#1D9E75',
  'advent-lent': '#534AB7',
  red: '#A32D2D',
  rose: '#D4537E',
  feast: '#BA7517',
};

// romcal season value -> our chrome season. Christmas and Eastertide are white
// seasons; since gold/white backgrounds are deliberately excluded, they resolve
// 'feast', which moves only the accent and leaves chrome on the indigo default.
const SEASON_MAP = {
  'Ordinary Time': 'ordinary',
  Advent: 'advent-lent',
  Lent: 'advent-lent',
  'Holy Week': 'advent-lent',
  Christmas: 'feast',
  Eastertide: 'feast',
};

// Cache the computed calendar per year — romcal builds all 365 days at once.
const calendarCache = new Map();

function getCalendar(year) {
  if (!calendarCache.has(year)) {
    calendarCache.set(year, Romcal.calendarFor({ year, country: 'unitedStates', locale: 'en' }));
  }
  return calendarCache.get(year);
}

// Local calendar date as YYYY-MM-DD. romcal keys entries to UTC midnight, so
// comparing against a UTC-derived string here would shift the day for anyone
// west of Greenwich (Central Time is UTC-5/-6).
function localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function entryDateKey(entry) {
  // Entries are UTC midnight, so the UTC slice is the intended calendar date.
  return new Date(entry.moment).toISOString().slice(0, 10);
}

/**
 * Resolve the liturgical season and colors for a given date.
 * @param {Date} [date] defaults to today
 * @returns {{season: string|null, color: string, accent: string|null, name?: string}}
 */
export function getLiturgicalSeasonForDate(date = new Date()) {
  try {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return NO_SEASON;

    const key = localDateKey(date);
    const entry = getCalendar(date.getFullYear()).find(e => entryDateKey(e) === key);
    if (!entry) return NO_SEASON;

    const romcalColor = entry.data?.meta?.liturgicalColor?.key;
    const romcalSeason = entry.data?.season?.value;
    if (!romcalColor || !romcalSeason) return NO_SEASON;

    const name = entry.name;

    // Gaudete / Laetare — violet chrome (they sit inside Advent and Lent) with
    // a rose accent.
    if (romcalColor === 'ROSE') {
      return { season: 'rose', color: SEASON_COLORS.rose, accent: 'rose', name };
    }

    if (romcalColor === 'RED') {
      // Seasonal red — Palm Sunday, Good Friday, Pentecost. Full red chrome.
      if (romcalSeason !== 'Ordinary Time') {
        return { season: 'red', color: SEASON_COLORS.red, accent: null, name };
      }
      // Sanctoral red — martyr memorials during Ordinary Time. These are
      // single-day observances, so chrome stays green and only the accent moves.
      return { season: 'ordinary', color: SEASON_COLORS.red, accent: 'red', name };
    }

    const season = SEASON_MAP[romcalSeason];
    if (!season) return NO_SEASON;

    // A white day inside Ordinary Time (All Saints, Assumption, ...) is a
    // single-day feast: green chrome, gold accent.
    const accent = romcalColor === 'WHITE' && season === 'ordinary' ? 'gold' : null;

    return { season, color: SEASON_COLORS[season], accent, name };
  } catch {
    // Any romcal failure holds the default indigo theme.
    return NO_SEASON;
  }
}

// Options for the manual season picker. Only seasons whose [data-season] block
// in index.css sets --dh-primary belong here — otherwise the user picks an
// option and the chrome doesn't move. That rules out 'feast', which sets only
// --dh-accent (gold/white chrome is excluded by design; see SEASON_MAP above).
// 'rose' does set --dh-primary, but it renders as plain Advent/Lent violet with
// a rose accent, so it is left out as a redundant menu entry.
//
// This list constrains the MANUAL menu only. The automatic path above is
// untouched and still resolves feast, rose and every other season on their
// real days.
export const SEASON_OPTIONS = [
  { value: '', label: 'Rotating (auto)' },
  { value: 'ordinary', label: 'Ordinary Time' },
  { value: 'advent-lent', label: 'Advent / Lent' },
  { value: 'red', label: 'Solemn Red' },
];

/** Color only, for a given date. */
export function getLiturgicalColorForDate(date = new Date()) {
  return getLiturgicalSeasonForDate(date).color;
}

// Back-compat aliases — today's season/color.
export function getLiturgicalSeason() {
  return getLiturgicalSeasonForDate(new Date());
}

export function getLiturgicalColor() {
  return getLiturgicalSeasonForDate(new Date()).color;
}
